import React, { FC, useEffect, useState } from "react";
import { useLocation } from "react-router";

import { Badge, Box, keyframes, styled } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { ChangesetIcon } from "../../components/icons";
import Link from "../../components/styled/Link";
import { useSelector } from "../../store";
import {
  useGetSettingsQuery,
  useListActivityLogsQuery,
  useListChangesetsQuery,
} from "../../store/enhancedApi";
import { useApplyChangesetMutation } from "../../store/pomeriumApi";
import ActivityLogsList from "../../views/activityLogs/components/ActivityLogsList";

const Content = styled(Box)(({ theme }) => ({
  maxHeight: theme.spacing(50),
  overflow: "auto",
}));

const Footer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.neutral[900]
      : theme.palette.neutral[50],
  padding: theme.spacing(2),
}));

const ChangesetPopover: FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [showAnimation, setShowAnimation] = useState(false);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const { data: changesetsResult } = useListChangesetsQuery(
    {
      organizationId: organizationId,
      clusterId: clusterId,
      status: "pending",
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const changesets = changesetsResult?.rows || [];
  const { data: settings, isLoading: isSettingsLoading } = useGetSettingsQuery(
    {
      organizationId,
      clusterId,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const [applyChangeset] = useApplyChangesetMutation();
  const location = useLocation();

  const open = Boolean(anchorEl);
  const changeset = changesets?.find?.((x) => x.status === "pending");

  const { data: activityLogsResult } = useListActivityLogsQuery(
    {
      organizationId: organizationId,
      changesetId: changeset?.id,
    },
    {
      skip: !organizationId || !changeset,
    },
  );
  const activityLogs = activityLogsResult?.rows || [];

  useEffect(() => {
    setAnchorEl(null);
  }, [location?.pathname]);

  useEffect(() => {
    if (timerId) {
      clearInterval(timerId);
    }
    if (changeset) {
      const tId = setInterval(() => {
        setShowAnimation(true);
        setTimeout(() => setShowAnimation(false), 1000);
      }, 5000);
      setTimerId(tId);
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [showAnimation, changesets]);

  if (
    !organizationId ||
    !clusterId ||
    isSettingsLoading ||
    settings?.autoApplyChangesets
  ) {
    return <></>;
  }

  const handleClickApplyChangeset = () => {
    if (!changeset) {
      return;
    }
    (async () => {
      await applyChangeset({
        organizationId: organizationId,
        changesetId: changeset.id,
      }).unwrap();
      setAnchorEl(null);
    })();
  };

  const handleClickIcon = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!changeset) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const bounce = keyframes`
    0%, 50% { transform: translateY(0) scale(1); }
    4%, 44% { transform: translateY(4px) scale(1.05, 0.9); }
    13%, 38% { transform: translateY(-6px) scale(0.96, 1.03); }
    25% { transform: translateY(-5px) scale(1); }
`;

  const ChangeSetWrapper = styled(ChangesetIcon)`
    animation-name: ${bounce};
    animation-duration: 333ms;
    animation-direction: normal;
    animation-timing-function: ease-in;
    animation-iteration-count: infinite;
  `;

  return (
    <Stack>
      {!!changeset && (
        <IconButton onClick={handleClickIcon}>
          <Badge variant="dot" color="warning" invisible={!changeset}>
            {showAnimation && <ChangeSetWrapper color="secondary" />}
            {!showAnimation && <ChangesetIcon color="secondary" />}
          </Badge>
        </IconButton>
      )}
      {!changeset && (
        <Tooltip title="No Pending Changes.">
          <span>
            <ChangesetIcon color="secondary" />
          </span>
        </Tooltip>
      )}
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack>
          <Content>
            {activityLogs ? (
              <ActivityLogsList activityLogs={activityLogs} />
            ) : (
              <></>
            )}
          </Content>
          <Footer>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle2">
                  {activityLogs?.length === 0
                    ? "no changes are pending"
                    : activityLogs?.length === 1
                      ? "1 change is pending"
                      : `${activityLogs?.length} changes are pending`}
                </Typography>
                <Button variant="contained" onClick={handleClickApplyChangeset}>
                  <Typography>Apply Changeset</Typography>
                </Button>
              </Stack>
              <Typography variant="caption">
                You can enable auto application of changesets in{" "}
                <Link to="/app/settings">settings</Link>.
              </Typography>
            </Stack>
          </Footer>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default ChangesetPopover;
